import { Select, Text } from '@viapeople/components'

export interface DashboardFiltersValue {
	cycleIds: string[]
	departments: string[]
	businessUnits: string[]
	locations: string[]
	managerIds: string[]
	performanceCategories: Array<'High' | 'Solid' | 'Needs Improvement'>
	ratingMin?: number
	ratingMax?: number
}

interface DashboardFiltersProps {
	value: DashboardFiltersValue
	options: {
		cycles: Array<{ id: string; name: string }>
		departments: string[]
		businessUnits: string[]
		locations: string[]
		managers: Array<{ id: string; name: string }>
	}
	onChange: (next: DashboardFiltersValue) => void
	defaults: { cycleIds: string[] }
}

const CATEGORY_OPTIONS = [
	{ value: 'High', label: 'High (≥ 4)' },
	{ value: 'Solid', label: 'Solid (> 2, < 4)' },
	{ value: 'Needs Improvement', label: 'Needs Improvement (≤ 2)' },
]

const toMultiValue = (v: unknown): string[] => {
	if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string')
	if (typeof v === 'string') return v ? [v] : []
	return []
}

export function DashboardFilters({ value, options, onChange, defaults }: DashboardFiltersProps) {
	function update(partial: Partial<DashboardFiltersValue>) {
		onChange({ ...value, ...partial })
	}

	function clearAll() {
		onChange({
			cycleIds: defaults.cycleIds,
			departments: [],
			businessUnits: [],
			locations: [],
			managerIds: [],
			performanceCategories: [],
			ratingMin: undefined,
			ratingMax: undefined,
		})
	}

	return (
		<div
			data-testid='dashboard-filters'
			className='flex flex-wrap gap-3 p-3 border border-neutral-200 rounded bg-neutral-50'>
			<div className='flex flex-col gap-1' data-testid='filter-cycle'>
				<Text variant='hint' className='text-neutral-500'>
					Cycle
				</Text>
				<Select
					isMulti
					placeholder='Select cycle(s)'
					options={options.cycles.map((c) => ({ value: c.id, label: c.name }))}
					value={value.cycleIds}
					onChange={(v: unknown) => update({ cycleIds: toMultiValue(v) })}
				/>
			</div>
			<div className='flex flex-col gap-1' data-testid='filter-department'>
				<Text variant='hint' className='text-neutral-500'>
					Department
				</Text>
				<Select
					isMulti
					placeholder='All departments'
					options={options.departments.map((d) => ({ value: d, label: d }))}
					value={value.departments}
					onChange={(v: unknown) => update({ departments: toMultiValue(v) })}
				/>
			</div>
			<div className='flex flex-col gap-1' data-testid='filter-business-unit'>
				<Text variant='hint' className='text-neutral-500'>
					Business Unit
				</Text>
				<Select
					isMulti
					placeholder='All business units'
					options={options.businessUnits.map((b) => ({ value: b, label: b }))}
					value={value.businessUnits}
					onChange={(v: unknown) => update({ businessUnits: toMultiValue(v) })}
				/>
			</div>
			<div className='flex flex-col gap-1' data-testid='filter-location'>
				<Text variant='hint' className='text-neutral-500'>
					Location
				</Text>
				<Select
					isMulti
					placeholder='All locations'
					options={options.locations.map((l) => ({ value: l, label: l }))}
					value={value.locations}
					onChange={(v: unknown) => update({ locations: toMultiValue(v) })}
				/>
			</div>
			<div className='flex flex-col gap-1' data-testid='filter-manager'>
				<Text variant='hint' className='text-neutral-500'>
					Manager
				</Text>
				<Select
					isMulti
					placeholder='All managers'
					options={options.managers.map((m) => ({ value: m.id, label: m.name }))}
					value={value.managerIds}
					onChange={(v: unknown) => update({ managerIds: toMultiValue(v) })}
				/>
			</div>
			<div className='flex flex-col gap-1' data-testid='filter-performance-category'>
				<Text variant='hint' className='text-neutral-500'>
					Performance Category
				</Text>
				<Select
					isMulti
					placeholder='All categories'
					options={CATEGORY_OPTIONS}
					value={value.performanceCategories}
					onChange={(v: unknown) =>
						update({
							performanceCategories: toMultiValue(v) as Array<'High' | 'Solid' | 'Needs Improvement'>,
						})
					}
				/>
			</div>
			<div className='flex flex-col gap-1' data-testid='filter-rating-range'>
				<Text variant='hint' className='text-neutral-500'>
					Rating Range
				</Text>
				<div className='flex items-center gap-1'>
					<input
						data-testid='filter-rating-min'
						type='number'
						min={0}
						max={5}
						step={0.1}
						placeholder='min'
						className='w-16 border border-neutral-300 rounded px-2 py-1 text-xs'
						value={value.ratingMin ?? ''}
						onChange={(e) => {
							const next = e.target.value === '' ? undefined : Number(e.target.value)
							// Maintain min ≤ max so the range never represents an impossible
							// interval that would silently return zero rows everywhere.
							if (typeof next === 'number' && typeof value.ratingMax === 'number' && next > value.ratingMax) {
								update({ ratingMin: next, ratingMax: next })
							} else {
								update({ ratingMin: next })
							}
						}}
					/>
					<span className='text-neutral-500 text-xs'>—</span>
					<input
						data-testid='filter-rating-max'
						type='number'
						min={0}
						max={5}
						step={0.1}
						placeholder='max'
						className='w-16 border border-neutral-300 rounded px-2 py-1 text-xs'
						value={value.ratingMax ?? ''}
						onChange={(e) => {
							const next = e.target.value === '' ? undefined : Number(e.target.value)
							if (typeof next === 'number' && typeof value.ratingMin === 'number' && next < value.ratingMin) {
								update({ ratingMin: next, ratingMax: next })
							} else {
								update({ ratingMax: next })
							}
						}}
					/>
				</div>
			</div>
			<div className='flex flex-col justify-end'>
				<button
					type='button'
					data-testid='filter-clear'
					onClick={clearAll}
					className='text-xs px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-50'>
					Clear filters
				</button>
			</div>
		</div>
	)
}
